import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Loader from 'src/shared/components/loader/Loader';
import ErrorScreen from 'src/shared/components/error-screen/ErrorScreen';
import { useCustomersQuery } from 'src/app/customers/state/api/customersGraphSlice';
import { rtkQueryErrorToError } from 'src/shared/utils/errorHelpers';
import { useGetProductLicensingQuery } from 'src/app/admin/views/risk-portfolio/state/api/organisationEngineSlice';
import { defaultCustomerId, isGnpShown, landingPage, landingPath } from 'src/app/customers/landingPage';
import useAuthService from 'src/shared/hooks/useAuthService';
import { ProductKey, ProductVisibility } from 'src/shared/types/engine/products';
import loginStorageService from '../../services/login-storage-service';

const PostAuthRedirect = () => {
    const [redirectTo] = useState(() => loginStorageService.getRedirectPath());
    const [navigationError, setNavigationError] = useState<Error>();
    const { permissions } = useAuthService();
    const { currentData: allCustomers, isError: isCustomersQueryError, error: customersQueryError } = useCustomersQuery({});
    const customers = allCustomers?.customers;
    const customerId = customers ? defaultCustomerId(customers) : undefined;
    const productLicensing = useGetProductLicensingQuery(customerId ?? '', { skip: !customerId || Boolean(redirectTo) });

    useEffect(() => {
        if (redirectTo) {
            try {
                loginStorageService.clearRedirectPath();
                window.location.assign(decodeURIComponent(redirectTo));
            } catch (err) {
                setNavigationError(err as Error);
            }
        }
    }, [redirectTo]);

    // A stored page is opening, so a failure to load the organisations does not matter.
    const customersError =
        !redirectTo && isCustomersQueryError && !allCustomers
            ? rtkQueryErrorToError(customersQueryError, 'Failed to load your account. Please try again.')
            : undefined;
    const error = navigationError ?? customersError;
    const isWaiting = Boolean(redirectTo) || !customers || productLicensing.isFetching;

    // The same order as an org switch. A new login has no current page, so no product to stay in.
    // No organisation, or an engine failure, leaves no data, so it lands like a product at default.
    const landing = landingPage({
        hasPortfolio: isGnpShown({
            hasPortfolio: customers?.find(customer => customer.customerId === customerId)?.hasPortfolio ?? false,
            setting: productLicensing.data?.[ProductKey.GLOBAL_NATURE_PERFORMANCE],
            isInternal: permissions?.is_internal_user ?? false,
        }),
        isGnrVisible: productLicensing.data?.[ProductKey.GLOBAL_NATURE_RISK] === ProductVisibility.VISIBLE,
        wasOnGnpPage: false,
    });

    if (error) {
        return <ErrorScreen error={error} />;
    } else if (isWaiting) {
        return <Loader />;
    } else {
        return <Navigate replace to={landingPath(landing, customerId ?? '')} />;
    }
};

export default PostAuthRedirect;
